import { Ellipsis, FileInput, FileStack, Pin, Trash2 } from "lucide-react"

import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarShortcut,
  MenubarTrigger,
} from "@components/common/menu-bar"
import { DictType } from "@pma/types"
import { Separator } from "@components/common/separator"
import useTranslator from "@hooks/use-translator"
import DeleteTask from "./delete-task"
import { UpdateIcon } from "@radix-ui/react-icons"
import { parseCookies } from "nookies"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CONVERT_SUB_TASK_TO_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { useAuth } from "@pma/stores/auth-store"
import PinTask from "./pin-task"
import TransferProject from "./transfer-project"
import { useContext, useState } from "react"
import MoveTask from "./grouped-tasks/Move-task-group"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"
import RestoreTask from "./restore-task"

interface Props {
  task: DictType
  group_id?: null | number | undefined
}
const SubTaskMenu = ({ task, group_id }: Props) => {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [IsModalOpen, setIsModalOpen] = useState(false)
  const [IsTaskGroupOpen, setIsTaskGroupOpen] = useState(false)
  const userId = authConfig?.id
  const { groupedTasksRefetch } = useContext(GroupedTasksContext)

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const pinnedByUser = task?.pinned_by_user_id?.includes(userId)

  const convertTask = async () => {
    const response = await axios.post(
      SERVER_URL + CONVERT_SUB_TASK_TO_TASK_ENDPOINT,
      {
        subtasks_id: [task?.id],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const convertTaskMutation = useMutation({
    mutationKey: ["convert-task", task?.id],
    mutationFn: convertTask,
    onSuccess: (data) => {
      // Trigger Revalidate tasks
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      groupedTasksRefetch()
      toast({
        title: translate("Success"),
        description: `${task?.title} ${translate("has been converted to a task")}`,
        variant: "success",
      })
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to convert")} ${task?.title} ${translate("to task")}`,
        variant: "error",
      })
    },
  })

  if (task?.parent_tasks_id) {
    return (
      <div className=''>
        <Menubar className='border-none bg-transparent'>
          <MenubarMenu>
            <MenubarTrigger className='flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border bg-white p-0 text-black'>
              <Ellipsis size={16} />
            </MenubarTrigger>
            <MenubarContent className='mx-2 gap-2 bg-background'>
              <MenubarItem className='flex w-full cursor-pointer items-center justify-between p-2 text-xs'>
                <PinTask task={task} className='flex w-full items-center justify-between'>
                  <div className='flex w-full items-center'>
                    <span className='min-w-[180px] flex-1 text-left'>
                      {translate(pinnedByUser ? "Unpin" : "Pin")}
                    </span>
                    <Pin size={14} />
                  </div>
                </PinTask>
                <MenubarShortcut></MenubarShortcut>
              </MenubarItem>

              <Separator />
              <MenubarItem
                onClick={(e) => {
                  e.preventDefault()
                  convertTaskMutation.mutate()
                }}
                className='flex w-full cursor-pointer items-center justify-between p-2 text-xs'
              >
                <span className='flex-1'>{translate("Convert to task")}</span> <UpdateIcon />
                <MenubarShortcut></MenubarShortcut>
              </MenubarItem>

              <Separator />
              <MenubarItem
                onClick={(e) => {
                  e.preventDefault()
                }}
                className='flex items-center text-xs text-red-500'
              >
                <DeleteTask
                  className='flex w-full flex-row items-center justify-between'
                  task={task}
                >
                  <div className='flex w-full items-center'>
                    <span className='min-w-[180px] flex-1 text-left'>{translate("Delete")}</span>
                    <Trash2 size={14} />
                  </div>
                </DeleteTask>
                <MenubarShortcut></MenubarShortcut>
              </MenubarItem>

              <Separator />
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>
    )
  }

  return (
    <div className=''>
      {IsModalOpen && (
        <TransferProject
          IsModalOpen={IsModalOpen}
          setIsModalOpen={setIsModalOpen}
          task={task}
          project_id={task.project_id}
        />
      )}

      {IsTaskGroupOpen && (
        <MoveTask
          IsModalOpen={IsTaskGroupOpen}
          setIsModalOpen={setIsTaskGroupOpen}
          task={task}
          group_id={group_id}
        />
      )}

      <Menubar className='border-none bg-transparent'>
        <MenubarMenu>
          <MenubarTrigger className='flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border bg-white p-0 text-black'>
            <Ellipsis size={16} />
          </MenubarTrigger>
          <MenubarContent className='mx-2 gap-2 bg-background'>
            {task.deleted &&
              (isOrganizationOwner || isAdmin || task.deleted_by === authConfig.id) ? (
              <MenubarItem
                onClick={(e) => e.preventDefault()}
                className='flex items-center text-xs'
              >
                <RestoreTask task={task}>
                  <div className='flex w-full items-center'>
                    <span className='min-w-[180px] flex-1 text-left'>
                      {translate("Restore Tasks")}
                    </span>
                  </div>
                </RestoreTask>
              </MenubarItem>
            ) : (
              !task.deleted ?
                <>
                  <MenubarItem className='flex w-full cursor-pointer p-2 text-xs'>
                    <PinTask task={task} className='flex w-full items-center justify-between'>
                      <div className='flex w-full items-center'>
                        <span className='min-w-[180px] flex-1 text-left'>
                          {translate(pinnedByUser ? "Unpin" : "Pin")}
                        </span>
                        <Pin size={14} />
                      </div>
                    </PinTask>
                    <MenubarShortcut></MenubarShortcut>
                  </MenubarItem>
                  <Separator />
                  <MenubarItem
                    onClick={(e) => {
                      e.preventDefault()
                    }}
                    className='flex items-center text-xs text-red-500'
                  >
                    <DeleteTask
                      className='flex w-full flex-row items-center justify-between'
                      task={task}
                    >
                      <div className='flex w-full items-center'>
                        <span className='min-w-[180px] flex-1 text-left'>{translate("Delete")}</span>
                        <Trash2 size={14} />
                      </div>
                    </DeleteTask>
                    <MenubarShortcut></MenubarShortcut>
                  </MenubarItem>
                  <Separator />
                  <MenubarItem
                    onClick={(e) => {
                      e.preventDefault()
                      setIsModalOpen(!IsModalOpen)
                    }}
                    className='flex items-center text-xs'
                  >
                    <div className='flex w-full items-center'>
                      <span className='min-w-[180px] flex-1 text-left'>
                        {translate("Move task to")}{" "}
                        <b className='text-red-600'>{translate("other project")}</b>
                      </span>
                      <FileInput size={14} />
                    </div>
                  </MenubarItem>
                  {group_id !== undefined && (
                    <>

                      <Separator />
                      <MenubarItem
                        onClick={(e) => {
                          e.preventDefault()
                          setIsTaskGroupOpen(!IsTaskGroupOpen)
                        }}
                        className='flex items-center text-xs'
                      >
                        <div className='flex w-full items-center'>
                          <span className='min-w-[180px] flex-1 text-left'>
                            {translate("Move task to")}{" "}
                            <b className='text-red-600'>{translate("Task group")}</b>
                          </span>
                          <FileStack size={14} />
                        </div>
                      </MenubarItem>
                    </>
                  )}
                  <Separator />
                </> : null
            )}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  )
}
export default SubTaskMenu
